import {
  createElement,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';

import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

interface RevealProps {
  /** Tag to render. Defaults to `div`. Use `li` / `article` / `header`
   *  when the wrapped element is itself the layout child. */
  as?: ElementType;
  children: ReactNode;
  /** Delay (ms) before the fade-up begins — useful for cascading
   *  reveal of siblings in a grid. */
  delay?: number;
  /** Extra classes passed through to the rendered tag. */
  className?: string;
  /** Vertical translation (px) the element starts at before reveal. */
  y?: number;
  /** Style overrides — merged with the reveal animation styles. */
  style?: CSSProperties;
}

/**
 * Fades + slides a block of content into view when it scrolls into
 * the viewport. Uses an IntersectionObserver, fires once, then
 * disconnects so further scrolling is free.
 *
 * Bypasses the animation entirely under `prefers-reduced-motion:
 * reduce` — the content renders in place, fully opaque, from the
 * first paint.
 */
export default function Reveal({
  as = 'div',
  children,
  delay = 0,
  className,
  y = 24,
  style,
}: RevealProps) {
  const reduce = usePrefersReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState<boolean>(reduce);

  useEffect(() => {
    if (reduce) {
      setVisible(true);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      // Threshold 0.12 + a small negative bottom margin keeps the
      // reveal from firing on near-misses (e.g., bottom 10px of an
      // off-screen card peeking in).
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [reduce]);

  const animationStyle: CSSProperties = reduce
    ? {}
    : {
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : `translate3d(0, ${y}px, 0)`,
        transition: `opacity 720ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 720ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
        willChange: visible ? 'auto' : 'opacity, transform',
      };

  return createElement(
    as,
    {
      ref,
      className,
      style: { ...style, ...animationStyle },
    },
    children,
  );
}
